import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { Button, EventCard, CardLoader } from 'app/components';
import { CaretLeft, CaretRight, Ticket } from '@phosphor-icons/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import useLoginModal from '../../../login/useLoginModal';
import 'swiper/swiper-bundle.css';

import './style';
import { analyticEventLogger } from '../../../../utils/commonUtils';
import { SELLER_TICKET_NAVBAR_EVENT_CLICK } from '../../../../constants/eventLogger';


const Trending = ({
  title,
  resale = false,
  btnLabel,
  isBanner = false,
  trendingEvents = [],
  placeholder,
}) => {
  const [nextEl, setNextEl] = useState(null);
  const [prevEl, setPrevEl] = useState(null);
  const swiperRef = useRef(null);
  const [isHovered, setIsHovered] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginModal = useLoginModal();

  const { userData, activeRequests } = useSelector((state) => state.app);

  const isEventsLoading = activeRequests?.includes('get-events-list-landing');
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.navigation?.update();
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      const slidesPerView =
        windowWidth >= 1024 ? 'auto' : windowWidth > 650 ? 2 : 1;
      // swiperRef.current.params?.slidesPerView = slidesPerView;
      // swiperRef.current.update();
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isMobileView = window.innerWidth < 650;
  const isTabView = window.innerWidth < 1024;

  let slides = 3;
  if (isMobileView) {
    slides = 1;
  } else if (isTabView) {
    slides = 2;
  }

  const handleLabelClick = () => {
    navigate('/all-events', { state: resale ? 'secondary' : 'primary' });
  };

  const handleCardHover = (id) => {
    setIsHovered((prevIsHovered) => ({
      ...prevIsHovered,
      [id]: true,
    }));
    swiperRef.current.autoplay.stop();
  };

  const handleCardHoverLeave = (id) => {
    setIsHovered((prevIsHovered) => ({
      ...prevIsHovered,
      [id]: false,
    }));
    const isAnyCardHovered = Object.values(isHovered).some(
      (hovered) => hovered,
    );
    if (!isAnyCardHovered) {
      swiperRef.current.autoplay.start();
    }
  };

  const onClickResell = () => {
    analyticEventLogger(SELLER_TICKET_NAVBAR_EVENT_CLICK, {
      isAuthenticated: !!userData?.email,
    });
    if (!userData?.email) {
      loginModal.onOpen();
      loginModal.callback.current = () => navigate('/sell-tickets');
    }
    else {
      mixpanel.track("Landing_Resell_Tickets_Clicked", {
       "event_id": null, 
       "event_name": "N/A",
       "user_id": userData?._id || "Anonymous",
       "page_url": window.location.href,
      });

      navigate('/sell-tickets');
    }
  };

  return (
    <>
      <div
        className="trendingContainer"
        style={{
          paddingRight:
            window.innerWidth < 650 && trendingEvents?.length === 0
              ? '24px'
              : '',
        }}
      >
        <div className="self-stretch flex flex-row items-center justify-between">
          <div className="relative whitespace-nowrap text-black tracking-[-0.02em] leading-[40px] font-semibold text-2xl sm:text-2xl not-italic md:text-4xl font-general-sans">
            {title}
          </div>

          {isMobileView ? null : (
            <Button
              onClick={handleLabelClick}
            >
              {btnLabel}
            </Button>
          )}
        </div>

        {isEventsLoading ? (
          <CardLoader count={3} type="event" className="self-stretch flex flex-row items-center w-[1130px] justify-start text-sm text-green-text relative px-0 md:px-2" />
        ) : 
          <div className="self-stretch flex flex-row items-center w-full justify-start text-sm text-green-text relative px-0 md:px-2">
            {trendingEvents.length > slides && window.innerWidth > 650 && (
              <button
                className="absolute left-[-50px] p-[6px] bg-grey-50 rounded-3xl"
                onClick={() => swiperRef.current.slidePrev()}
              >
                <CaretLeft size={16.8}
                  color="white"
                  alt="next" />
              </button>
            )}

            {trendingEvents?.length > 0 ? (
              <Swiper
                className={`min-h-[250px] ${trendingEvents?.length > 2 ? 'sw' : ''}`}
                navigation={{
                  prevEl: prevEl,
                  nextEl: nextEl,
                }}
                spaceBetween={20}
                slidesPerView={Math.min(trendingEvents.length, slides)}
                autoplay={
                  window.innerWidth > 650 && {
                    delay: 2000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }
                }
                speed={1000}
                loop={true}
                modules={[Navigation, Autoplay]}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
                style={{
                  margin: 'unset',
                }}
              >
                {trendingEvents?.map((event) => {
                  const startPrice = !resale 
                    ? event?.smp_detail_entity_set?.length 
                      ? Math.min(...event.smp_detail_entity_set.map(p => p.price)) 
                      : 0 
                    : event.start_price;

                  return (
                    <SwiperSlide key={event.id}>
                      <EventCard
                        height="358px"
                        url={event.image}
                        id={event._id}
                        eventName={event.name}
                        eventDate={event.start_date}
                        startPrice={startPrice}
                        eventSlug={event._id}
                        eventCity={event.venue || event?.event_address?.name || ''}
                        eventType={event.type}
                        resale={event.is_secondary}
                        isTrending={event.is_trending}
                        onClick={() => {
                          mixpanel.track("Event_Clicked", {
                            "event_id": event._id,                  
                            "event_name": event.name,             
                            "user_id": userData?._id || "Anonymous",    
                            "page_url": window.location.href,  
                          });
                          navigate(resale? `/${event._id}` : `/event/${event?.id}`);
                        }}
                        onMouseEnter={() => handleCardHover(event._id)}
                        onMouseLeave={() => handleCardHoverLeave(event._id)}
                      />
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            ) : (
              <div className="noeventscontainer">
                <img
                  src="assets/images/notrendingevents.svg"
                  alt="Oops, no trending events near you!"
                  className="noeventsimage"
                />
                <div className="text-16 text-center">
                  {placeholder || 'Oops, no trending events near you!'}
                </div>
                {!placeholder && (<Button
                  className="cursor-pointer mt-6"
                  onClick={() => navigate('/request')}
                >
                  Request an event for resale
                </Button>)}
              </div>
            )}
            {trendingEvents.length > slides && window.innerWidth > 650 && (
              <button
                className="absolute right-[-50px] p-[6px] bg-grey-50 rounded-3xl"
                onClick={() => swiperRef.current.slideNext()}
              >
                <CaretRight size={16.8}
                  color="white"
                  alt="next" />
              </button>
            )}
          </div>
        }
        {isMobileView && (
          <Button
            onClick={handleLabelClick}
          >
            {btnLabel}
          </Button>
        )}

        {isBanner && window.innerWidth > 650 && (
          <div className="flex w-full p-[24px] bg-grey-5 rounded-[13px]">
            <div className="flex gap-[24px] items-start">
              <div className="w-[68px] h-[68px] justify-center items-center p-[14.571px] bd">
                <Ticket size={39}
                  color="#878787"
                  className="bg-white" />
              </div>
              <div className="flex flex-col gap-2">
                <h1 className="text-xl not-italic font-semibold leading-[125%]">
                  Got extra tickets to an event?
                </h1>
                <p className="text-16">List them now to find buyers soon.</p>
              </div>
            </div>
            <Button
              className="ml-auto"
              onClick={onClickResell}
            >
              Sell tickets now
            </Button>
          </div>
        )}
      </div>

      {isBanner && window.innerWidth < 650 && (
        <div
          className="flex flex-col w-auto p-[24px] bg-grey-5 rounded-[13px] mx-6 mb-10"
          style={{ marginBottom: '40px' }}
        >
          <div className="flex flex-col gap-[24px] items-start md:items-center">
            <div className="w-[68px] h-[68px] justify-center items-center p-[14.571px] bd">
              <Ticket size={39}
                color="#878787"
                className="bg-white" />
            </div>
            <div className="flex flex-col gap-2 mb-5">
              <h1 className="text-xl not-italic font-semibold leading-[125%]">
                Got extra tickets to an event?
              </h1>
              <p className="text-16">List them now to find buyers soon.</p>
            </div>
          </div>
          <Button
            className=" w-full md:w-none md:ml-auto"
            onClick={onClickResell}
          >
            Sell tickets now
          </Button>
        </div>
      )}
    </>
  );
};

export default Trending;
